import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Minus, ShoppingCart, Calculator, Sparkles, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

// Importar imágenes reales de productos VillaTech
import droneE88 from '@/assets/products/drone-e88.jpg';
import smartKeyboardCase from '@/assets/products/smart-keyboard-case.jpg';
import samsungLeatherCase from '@/assets/products/samsung-leather-case.jpg';
import gameProjector from '@/assets/products/game-projector.jpg';
import magneticCharger from '@/assets/products/magnetic-charger.jpg';
import metalStand from '@/assets/products/metal-stand.jpg';
import fengaiC60 from '@/assets/products/fengai-c60.jpg';
import droneK12Max from '@/assets/products/drone-k12max.jpg';

interface Product {
  id: string;
  name: string;
  category: string;
  originalPrice: number;
  image: string;
}

type MembershipType = 'bronce' | 'plata' | 'oro';

// Descuentos por categoría según membresía (VillaTech)
const discountsByMembership: Record<MembershipType, Record<string, number>> = {
  bronce: {
    'Tecnología': 5,
    'Audio': 5,
    'Gaming': 5,
    'Estuches': 5,
    'Relojes': 5,
    'Cargadores': 5,
    'Reparaciones': 10,
    'Vidrios': 5,
  },
  plata: {
    'Tecnología': 10,
    'Audio': 10,
    'Gaming': 10,
    'Estuches': 10,
    'Relojes': 10,
    'Cargadores': 10,
    'Reparaciones': 15,
    'Vidrios': 10,
  },
  oro: {
    'Tecnología': 20,
    'Audio': 15,
    'Gaming': 20,
    'Estuches': 20,
    'Relojes': 10,
    'Cargadores': 20,
    'Reparaciones': 20,
    'Vidrios': 15,
  },
};

// Precios mensuales por membresía
const monthlyPrices: Record<MembershipType, number> = {
  bronce: 12000,
  plata: 27000,
  oro: 45000,
};

// Colores por membresía
const membershipColors: Record<MembershipType, { 
  primary: string; 
  bg: string; 
  border: string; 
  text: string;
  badge: string;
  button: string;
  buttonHover: string;
}> = {
  bronce: {
    primary: 'text-amber-400',
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/40',
    text: 'text-amber-400',
    badge: 'bg-amber-600 text-white',
    button: 'bg-amber-500/20 text-amber-400 hover:bg-amber-500 hover:text-black border-amber-500/40',
    buttonHover: 'hover:border-amber-500/50',
  },
  plata: {
    primary: 'text-slate-300',
    bg: 'bg-slate-500/10',
    border: 'border-slate-500/40',
    text: 'text-slate-300',
    badge: 'bg-slate-500 text-white',
    button: 'bg-slate-500/20 text-slate-300 hover:bg-slate-500 hover:text-white border-slate-500/40',
    buttonHover: 'hover:border-slate-500/50',
  },
  oro: {
    primary: 'text-yellow-400',
    bg: 'bg-yellow-500/10',
    border: 'border-yellow-500/40',
    text: 'text-yellow-400',
    badge: 'bg-yellow-500 text-black',
    button: 'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500 hover:text-black border-yellow-500/40',
    buttonHover: 'hover:border-yellow-500/50',
  },
};

const membershipNames: Record<MembershipType, string> = {
  bronce: 'Bronce',
  plata: 'Plata',
  oro: 'Oro',
};

// Productos reales de VillaTech
const products: Product[] = [
  // Tecnología
  { id: 'tech-drone-1', name: 'Drone E88', category: 'Tecnología', originalPrice: 22000, image: droneE88 },
  { id: 'tech-drone-2', name: 'Drone K12MAX', category: 'Tecnología', originalPrice: 46000, image: droneK12Max },
  { id: 'tech-1', name: 'Alexa Echo Dot 5ta Gen', category: 'Tecnología', originalPrice: 35000, image: 'https://images.unsplash.com/photo-1543512214-318c7553f230?w=200&h=200&fit=crop' },
  { id: 'tech-2', name: 'Amazon Fire TV Stick', category: 'Tecnología', originalPrice: 29000, image: 'https://images.unsplash.com/photo-1544244015-0df4b3ffc6b0?w=200&h=200&fit=crop' },
  { id: 'tech-3', name: 'Amazon Fire TV Stick 4K', category: 'Tecnología', originalPrice: 39000, image: 'https://images.unsplash.com/photo-1593359677879-a4bb92f829d1?w=200&h=200&fit=crop' },
  { id: 'tech-4', name: 'Chromecast 4ta Gen 4K', category: 'Tecnología', originalPrice: 49000, image: 'https://images.unsplash.com/photo-1611162616475-46b635cb6868?w=200&h=200&fit=crop' },
  { id: 'tech-5', name: 'Alexa Echo Show 8', category: 'Tecnología', originalPrice: 65000, image: 'https://images.unsplash.com/photo-1512446816042-444d641267d4?w=200&h=200&fit=crop' },
  
  // Audio
  { id: 'audio-1', name: 'Parlante Hypergear Wave', category: 'Audio', originalPrice: 32000, image: 'https://images.unsplash.com/photo-1608043152269-423dbba4e7e1?w=200&h=200&fit=crop' },
  { id: 'audio-2', name: 'Parlante E-Device E33', category: 'Audio', originalPrice: 28000, image: 'https://images.unsplash.com/photo-1589003077984-894e133dabab?w=200&h=200&fit=crop' },
  { id: 'audio-3', name: 'Parlante Bluetooth Animado', category: 'Audio', originalPrice: 22000, image: 'https://images.unsplash.com/photo-1545454675-3531b543be5d?w=200&h=200&fit=crop' },
  { id: 'audio-4', name: 'Micrófono Solapa Lightning', category: 'Audio', originalPrice: 12000, image: 'https://images.unsplash.com/photo-1590602847861-f357a9332bbc?w=200&h=200&fit=crop' },
  
  // Gaming
  { id: 'gaming-projector', name: 'Game Projector 2 in 1', category: 'Gaming', originalPrice: 66000, image: gameProjector },
  { id: 'gaming-1', name: 'Audífonos Xtrike Me Bluetooth', category: 'Gaming', originalPrice: 26000, image: 'https://images.unsplash.com/photo-1618366712010-f4ae9c647dcb?w=200&h=200&fit=crop' },
  { id: 'gaming-2', name: 'Base Cargador PS5', category: 'Gaming', originalPrice: 33000, image: 'https://images.unsplash.com/photo-1606144042614-b2417e99c4e3?w=200&h=200&fit=crop' },
  
  // Estuches
  { id: 'case-keyboard', name: 'Smart Keyboard Case', category: 'Estuches', originalPrice: 14000, image: smartKeyboardCase },
  { id: 'case-samsung', name: 'Samsung Leather Case', category: 'Estuches', originalPrice: 10000, image: samsungLeatherCase },
  { id: 'case-1', name: 'OtterBox Defender S21 FE', category: 'Estuches', originalPrice: 16000, image: 'https://images.unsplash.com/photo-1601593346740-925612772716?w=200&h=200&fit=crop' },
  { id: 'case-2', name: 'OtterBox Samsung A72', category: 'Estuches', originalPrice: 18000, image: 'https://images.unsplash.com/photo-1592899677977-9c10ca588bbd?w=200&h=200&fit=crop' },
  
  // Relojes Inteligentes
  { id: 'watch-1', name: 'Reloj Earldom SW22 AMOLED', category: 'Relojes', originalPrice: 44000, image: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=200&h=200&fit=crop' },
  { id: 'watch-2', name: 'Reloj GW-X5 Pro Max', category: 'Relojes', originalPrice: 35000, image: 'https://images.unsplash.com/photo-1546868871-7041f2a55e12?w=200&h=200&fit=crop' },
  { id: 'watch-3', name: 'Reloj Inteligente DT88', category: 'Relojes', originalPrice: 34000, image: 'https://images.unsplash.com/photo-1579586337278-3befd40fd17a?w=200&h=200&fit=crop' },
  
  // Cargadores y Cables
  { id: 'charger-magnetic', name: 'Magnetic Wireless Charger Remax', category: 'Cargadores', originalPrice: 19000, image: magneticCharger },
  { id: 'charger-stand', name: 'Metal Rotatory Stand RM-C38', category: 'Cargadores', originalPrice: 10000, image: metalStand },
  { id: 'charger-powerbank', name: 'Fengai C60 Power Bank', category: 'Cargadores', originalPrice: 54000, image: fengaiC60 },
  { id: 'charger-2', name: 'Adaptador 25W PD', category: 'Cargadores', originalPrice: 16000, image: 'https://images.unsplash.com/photo-1588872657578-7efd1f1555ed?w=200&h=200&fit=crop' },
  
  // Reparaciones
  { id: 'repair-1', name: 'Cambio de Pantalla iPhone', category: 'Reparaciones', originalPrice: 45000, image: 'https://images.unsplash.com/photo-1597673030062-0a0f1a801a31?w=200&h=200&fit=crop' },
  { id: 'repair-2', name: 'Cambio de Batería', category: 'Reparaciones', originalPrice: 25000, image: 'https://images.unsplash.com/photo-1609081219090-a6d81d3085bf?w=200&h=200&fit=crop' },
  { id: 'repair-3', name: 'Reparación de Puerto', category: 'Reparaciones', originalPrice: 18000, image: 'https://images.unsplash.com/photo-1581092160607-ee22621dd758?w=200&h=200&fit=crop' },
  
  // Vidrios
  { id: 'glass-1', name: 'Vidrio Templado HD', category: 'Vidrios', originalPrice: 5000, image: 'https://images.unsplash.com/photo-1585771724684-38269d6639fd?w=200&h=200&fit=crop' },
  { id: 'glass-2', name: 'Vidrio Templado Premium', category: 'Vidrios', originalPrice: 8000, image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=200&h=200&fit=crop' },
];

const categories = ['Todos', 'Tecnología', 'Audio', 'Gaming', 'Estuches', 'Relojes', 'Cargadores', 'Reparaciones', 'Vidrios'];

interface CartItem {
  product: Product;
  quantity: number;
}

const formatPrice = (price: number) => {
  return `₡${price.toLocaleString('es-CR')}`;
};

export const ProductDiscountCalculator: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('Todos');
  const [selectedMembership, setSelectedMembership] = useState<MembershipType>('bronce');
  const [cart, setCart] = useState<CartItem[]>([]);

  const colors = membershipColors[selectedMembership];

  const getDiscount = (category: string) => {
    return discountsByMembership[selectedMembership][category] || 5;
  };

  const filteredProducts = selectedCategory === 'Todos' 
    ? products 
    : products.filter(p => p.category === selectedCategory);

  const addToCart = (product: Product) => {
    setCart(prev => {
      const existing = prev.find(item => item.product.id === product.id);
      if (existing) {
        return prev.map(item => 
          item.product.id === product.id 
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { product, quantity: 1 }];
    });
  };

  const removeFromCart = (productId: string) => {
    setCart(prev => {
      const existing = prev.find(item => item.product.id === productId);
      if (existing && existing.quantity > 1) {
        return prev.map(item => 
          item.product.id === productId 
            ? { ...item, quantity: item.quantity - 1 }
            : item
        );
      }
      return prev.filter(item => item.product.id !== productId);
    });
  };

  const clearCart = () => setCart([]);

  const totalOriginal = cart.reduce((sum, item) => sum + (item.product.originalPrice * item.quantity), 0);
  const totalWithDiscount = cart.reduce((sum, item) => {
    const discount = getDiscount(item.product.category);
    const discounted = item.product.originalPrice * (1 - discount / 100);
    return sum + (discounted * item.quantity);
  }, 0);
  const totalSavings = totalOriginal - totalWithDiscount;

  return (
    <section className="py-20 bg-gradient-to-b from-black to-zinc-900">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <Badge className={`mb-4 ${colors.bg} ${colors.text} ${colors.border} border`}>
            <Calculator className="w-4 h-4 mr-2" />
            Calculadora de Ahorro
          </Badge>
          <h2 className="text-3xl md:text-5xl font-bold mb-4">
            <span className={colors.primary}>Calculá tu ahorro</span>
          </h2>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            Seleccioná tu membresía y agregá productos para ver cuánto ahorrás
          </p>
        </motion.div>

        {/* Membership Selector */}
        <div className="flex flex-col items-center gap-4 mb-10">
          <div className="flex justify-center gap-3">
            {(['bronce', 'plata', 'oro'] as MembershipType[]).map((membership) => {
              const isSelected = selectedMembership === membership;
              const mColors = membershipColors[membership];
              return (
                <button
                  key={membership}
                  onClick={() => setSelectedMembership(membership)}
                  className={`px-6 py-3 rounded-xl font-bold text-sm transition-all ${
                    isSelected
                      ? `${mColors.badge} shadow-lg`
                      : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                  }`}
                >
                  {membershipNames[membership]}
                </button>
              );
            })}
          </div>
          
          {/* Precio mensual de la membresía seleccionada */}
          <div className={`${colors.bg} ${colors.border} border rounded-xl px-6 py-3`}>
            <p className="text-zinc-400 text-sm">¿Preferís mensual?</p>
            <p className={`${colors.text} font-bold text-lg`}>
              {membershipNames[selectedMembership]}: {formatPrice(monthlyPrices[selectedMembership])}/mes
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Product Selection */}
          <div className="lg:col-span-2">
            {/* Category Filter */}
            <div className="flex flex-wrap gap-2 mb-6">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedCategory === cat
                      ? `${colors.badge}`
                      : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Products Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {filteredProducts.map(product => {
                const discount = getDiscount(product.category);
                const discountedPrice = product.originalPrice * (1 - discount / 100);
                const cartItem = cart.find(item => item.product.id === product.id);
                
                return (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className={`bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden ${colors.buttonHover} transition-all group`}
                  >
                    <div className="relative h-32 overflow-hidden">
                      <img 
                        src={product.image} 
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                      <Badge className={`absolute top-2 right-2 ${colors.badge} text-xs`}>
                        -{discount}%
                      </Badge>
                    </div>
                    
                    <div className="p-4">
                      <p className={`text-xs ${colors.text} mb-1`}>{product.category}</p>
                      <h3 className="text-white font-medium text-sm mb-2 line-clamp-2">{product.name}</h3>
                      
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-zinc-500 line-through text-xs">{formatPrice(product.originalPrice)}</span>
                        <span className={`${colors.text} font-bold`}>{formatPrice(Math.round(discountedPrice))}</span>
                      </div>
                      
                      {cartItem ? (
                        <div className="flex items-center justify-between bg-zinc-800 rounded-lg px-2 py-1">
                          <button 
                            onClick={() => removeFromCart(product.id)}
                            className="p-1 hover:bg-zinc-700 rounded"
                          >
                            <Minus className="w-4 h-4 text-white" />
                          </button>
                          <span className="text-white font-bold">{cartItem.quantity}</span>
                          <button 
                            onClick={() => addToCart(product)}
                            className="p-1 hover:bg-zinc-700 rounded"
                          >
                            <Plus className="w-4 h-4 text-white" />
                          </button>
                        </div>
                      ) : (
                        <Button
                          onClick={() => addToCart(product)}
                          size="sm"
                          className={`w-full ${colors.button} border`}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Agregar
                        </Button>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Cart Summary */}
          <div className="lg:col-span-1">
            <div className={`bg-zinc-900 border ${colors.border} rounded-3xl p-6 sticky top-6`}>
              <div className="flex items-center justify-between mb-6">
                <h3 className={`text-xl font-bold text-white flex items-center gap-2`}>
                  <ShoppingCart className={`w-5 h-5 ${colors.text}`} />
                  Tu Carrito
                </h3>
                {cart.length > 0 && (
                  <button
                    onClick={clearCart}
                    className="text-zinc-500 hover:text-red-400 transition-colors"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                )}
              </div>

              {/* Membership indicator */}
              <div className={`mb-4 p-3 rounded-xl ${colors.bg} ${colors.border} border`}>
                <p className="text-zinc-400 text-sm">Membresía seleccionada:</p>
                <p className={`${colors.text} font-bold text-lg`}>{membershipNames[selectedMembership]}</p>
              </div>

              {cart.length === 0 ? (
                <div className="text-center py-8">
                  <ShoppingCart className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                  <p className="text-zinc-500">Agregá productos para calcular tu ahorro</p>
                </div>
              ) : (
                <>
                  <div className="space-y-3 max-h-64 overflow-y-auto mb-6">
                    {cart.map(item => {
                      const discount = getDiscount(item.product.category);
                      const discounted = item.product.originalPrice * (1 - discount / 100);
                      return (
                        <div key={item.product.id} className="flex items-center gap-3 bg-zinc-800/50 rounded-xl p-3">
                          <img 
                            src={item.product.image} 
                            alt={item.product.name}
                            className="w-12 h-12 rounded-lg object-cover"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm font-medium truncate">{item.product.name}</p>
                            <p className={`${colors.text} text-sm`}>{formatPrice(Math.round(discounted))} x {item.quantity}</p>
                          </div>
                          <div className="flex items-center gap-1">
                            <button 
                              onClick={() => removeFromCart(item.product.id)}
                              className="p-1 hover:bg-zinc-700 rounded"
                            >
                              <Minus className="w-3 h-3 text-zinc-400" />
                            </button>
                            <button 
                              onClick={() => addToCart(item.product)}
                              className="p-1 hover:bg-zinc-700 rounded"
                            >
                              <Plus className="w-3 h-3 text-zinc-400" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="border-t border-zinc-800 pt-4 space-y-3">
                    <div className="flex justify-between text-zinc-400">
                      <span>Precio sin membresía:</span>
                      <span className="line-through">{formatPrice(totalOriginal)}</span>
                    </div>
                    <div className="flex justify-between text-white">
                      <span>Precio con {membershipNames[selectedMembership]}:</span>
                      <span className={`${colors.text} font-bold`}>{formatPrice(Math.round(totalWithDiscount))}</span>
                    </div>
                    <div className="flex justify-between items-center bg-green-500/10 border border-green-500/30 rounded-xl p-3">
                      <span className="text-green-400 font-medium flex items-center gap-2">
                        <Sparkles className="w-4 h-4" />
                        ¡Ahorrás!
                      </span>
                      <span className="text-green-400 font-black text-xl">{formatPrice(Math.round(totalSavings))}</span>
                    </div>
                  </div>

                  <p className="text-xs text-zinc-500 text-center mt-4">
                    * Descuentos según membresía {membershipNames[selectedMembership]}
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};